<div class="bd mt-2">

  <!-- KPI row: NS + Rev.io side by side -->
  <div id="tour-kpi-row" class="bd__kpi-row">
    <section class="bd__block bd__kpi-panel">
      <div class="bd__title">
        <i class="pi pi-database" aria-hidden="true"></i>
        <span>NetSapiens KPIs</span>
      </div>
      <app-ns-kpis />
    </section>

    <section class="bd__block bd__kpi-panel">
      <div class="bd__title">
        <i class="pi pi-wallet" aria-hidden="true"></i>
        <span>Rev.io KPIs</span>
      </div>
      <app-revio-kpis />
    </section>

  </div>

  <!-- Billing Status Overview -->
  <section id="tour-overview-section" class="bd__block bd__overview">

    @if (activeView === 'overview') {
      <div class="bd__overview-header">
        <div class="bd__title">
          <i class="pi pi-table" aria-hidden="true"></i>
          <span>Billing Status Overview</span>
        </div>
        <div class="bd__overview-actions">
          <p-button
            id="tour-btn-manage"
            icon="pi pi-sitemap"
            label="Manage Domains"
            severity="secondary"
            (onClick)="openManage()"
          />
          @if (!overviewData) {
            <p-button
              id="tour-btn-load"
              icon="pi pi-play"
              label="Load"
              [disabled]="overviewLoading"
              (onClick)="loadOverview()"
            />
          } @else {
            <p-button
              icon="pi pi-cog"
              label="Columns"
              severity="info"
              (onClick)="overviewColOverlay.toggle($event)"
            />
            <p-overlayPanel #overviewColOverlay>
              <div class="bd__col-overlay">
                <div class="bd__col-overlay-title">Toggle columns</div>
                @for (col of overviewColumns; track col.field) {
                  <div class="bd__col-overlay-row">
                    <p-checkbox
                      [(ngModel)]="col.visible"
                      [binary]="true"
                      [inputId]="'ov-col-' + col.field"
                      (onChange)="onOverviewColChange()"
                    />
                    <label [for]="'ov-col-' + col.field" class="cursor-pointer">{{ col.header }}</label>
                  </div>
                }
              </div>
            </p-overlayPanel>
            <p-button
              icon="pi pi-download"
              label="Export"
              severity="success"
              pTooltip="Export to CSV"
              (onClick)="exportOverviewCsv()"
            />
            <p-button
              icon="pi pi-refresh"
              label="Refresh"
              severity="secondary"
              [disabled]="overviewLoading"
              pTooltip="Invalidates cache and reloads"
              (onClick)="refreshOverview()"
            />
          }
        </div>
      </div>

      @if (overviewLoading) {
        <div class="bd__overview-loading">
          <p-progressSpinner styleClass="bd__spinner" strokeWidth="4" />
          <span>Computing billing status for all domains… This may take a few minutes.</span>
        </div>
      } @else if (!overviewData) {
        <p class="bd__overview-empty">
          Click <strong>Load</strong> to generate the billing status overview for all enabled domains.
        </p>
      } @else {
        <div class="bd__overview-cached-row">
          <p class="bd__overview-cached">
            Last loaded: {{ overviewData.cachedAt | date:'MMM d, y h:mm a' }}
            &nbsp;·&nbsp; {{ filteredOverviewDomains.length }} of {{ overviewData.domains.length }} domain(s)
          </p>
          <div class="bd__overview-toggle" pTooltip="Only show domains with Mismatch, Missing in Rev.io, Extra in Rev.io, or Duplicated rows" tooltipPosition="top">
            <p-checkbox
              [(ngModel)]="overviewOnlyActionable"
              [binary]="true"
              inputId="overview-only-actionable"
            />
            <label for="overview-only-actionable" class="cursor-pointer">Needs action only</label>
          </div>
        </div>
        <p-table
          id="tour-overview-table"
          [value]="filteredOverviewDomains"
          [paginator]="true"
          [rows]="overviewRows"
          [rowsPerPageOptions]="[10, 20, 50, 100]"
          sortField="domain"
          [sortOrder]="1"
          styleClass="p-datatable-sm"
          (onPage)="onOverviewPageChange($event)"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pSortableColumn="domain">Domain <p-sortIcon field="domain" /></th>
              @if (isOverviewColVisible('customerName')) {
                <th>Rev.io Customer</th>
              }
              @if (isOverviewColVisible('description')) {
                <th>Description</th>
              }
              @if (isOverviewColVisible('totalRows')) {
                <th class="bd__col-count" pSortableColumn="totalRows">Products <p-sortIcon field="totalRows" /></th>
              }
              @if (isOverviewColVisible('matchCount')) {
                <th class="bd__col-count" pSortableColumn="matchCount">Match <p-sortIcon field="matchCount" /></th>
              }
              @if (isOverviewColVisible('mismatchCount')) {
                <th class="bd__col-count" pSortableColumn="mismatchCount">Mismatch <p-sortIcon field="mismatchCount" /></th>
              }
              @if (isOverviewColVisible('missingInRevioCount')) {
                <th class="bd__col-count" pSortableColumn="missingInRevioCount">Miss. Rev.io <p-sortIcon field="missingInRevioCount" /></th>
              }
              @if (isOverviewColVisible('extraInRevioCount')) {
                <th class="bd__col-count" pSortableColumn="extraInRevioCount">Extra Rev.io <p-sortIcon field="extraInRevioCount" /></th>
              }
              @if (isOverviewColVisible('duplicatedCount')) {
                <th class="bd__col-count" pSortableColumn="duplicatedCount">Dup <p-sortIcon field="duplicatedCount" /></th>
              }
              @if (isOverviewColVisible('staticServiceCount')) {
                <th class="bd__col-count" pSortableColumn="staticServiceCount">Static <p-sortIcon field="staticServiceCount" /></th>
              }
              @if (isOverviewColVisible('nonBillableCount')) {
                <th class="bd__col-count" pSortableColumn="nonBillableCount">Non-Billable <p-sortIcon field="nonBillableCount" /></th>
              }
              @if (isOverviewColVisible('trunkingCount')) {
                <th class="bd__col-count" pSortableColumn="trunkingCount">Trunking <p-sortIcon field="trunkingCount" /></th>
              }
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [class.bd__row--issue]="hasIssues(row)" [class.bd__row--unresolved]="row.customerId === null">
              <td
                [pTooltip]="row.description ?? ''"
                tooltipPosition="right"
                [tooltipDisabled]="!row.description"
              >{{ row.domain }}</td>
              @if (isOverviewColVisible('customerName')) {
                <td class="bd__col-customer">
                  @if (row.customerId === null) {
                    <p-tag
                      value="No customer found"
                      severity="warning"
                      pTooltip="Could not resolve a Rev.io customer for this domain. Set up a manual mapping to fix it."
                      tooltipPosition="top"
                    />
                  } @else {
                    {{ row.customerName ?? '—' }}
                  }
                </td>
              }
              @if (isOverviewColVisible('description')) {
                <td class="bd__col-description">{{ row.description ?? '—' }}</td>
              }
              @if (isOverviewColVisible('totalRows')) {
                <td class="bd__col-count">
                  @if (row.totalRows > 0) {
                    <p-tag [value]="row.totalRows" severity="secondary" />
                  } @else {
                    <span class="bd__zero">0</span>
                  }
                </td>
              }
              @if (isOverviewColVisible('matchCount')) {
                <td class="bd__col-count">
                  @if (row.matchCount > 0) {
                    <p-tag [value]="row.matchCount" severity="success" />
                  } @else {
                    <span class="bd__zero">0</span>
                  }
                </td>
              }
              @if (isOverviewColVisible('mismatchCount')) {
                <td class="bd__col-count">
                  @if (row.mismatchCount > 0) {
                    <p-tag [value]="row.mismatchCount" severity="warning" />
                  } @else {
                    <span class="bd__zero">0</span>
                  }
                </td>
              }
              @if (isOverviewColVisible('missingInRevioCount')) {
                <td class="bd__col-count">
                  @if (row.missingInRevioCount > 0) {
                    <p-tag [value]="row.missingInRevioCount" severity="danger" />
                  } @else {
                    <span class="bd__zero">0</span>
                  }
                </td>
              }
              @if (isOverviewColVisible('extraInRevioCount')) {
                <td class="bd__col-count">
                  @if (row.extraInRevioCount > 0) {
                    <p-tag [value]="row.extraInRevioCount" severity="danger" />
                  } @else {
                    <span class="bd__zero">0</span>
                  }
                </td>
              }
              @if (isOverviewColVisible('duplicatedCount')) {
                <td class="bd__col-count">
                  @if (row.duplicatedCount > 0) {
                    <p-tag [value]="row.duplicatedCount" severity="warning" />
                  } @else {
                    <span class="bd__zero">0</span>
                  }
                </td>
              }
              @if (isOverviewColVisible('staticServiceCount')) {
                <td class="bd__col-count">
                  @if (row.staticServiceCount > 0) {
                    <p-tag [value]="row.staticServiceCount" severity="info" />
                  } @else {
                    <span class="bd__zero">0</span>
                  }
                </td>
              }
              @if (isOverviewColVisible('nonBillableCount')) {
                <td class="bd__col-count">
                  @if (row.nonBillableCount > 0) {
                    <a routerLink="/billing/non-billable" [queryParams]="{ domain: row.domain }" class="bd__nb-link">
                      <p-tag [value]="row.nonBillableCount" severity="secondary" />
                    </a>
                  } @else {
                    <span class="bd__zero">0</span>
                  }
                </td>
              }
              @if (isOverviewColVisible('trunkingCount')) {
                <td class="bd__col-count">
                  @if (row.trunkingCount > 0) {
                    <p-tag [value]="row.trunkingCount" severity="secondary" pTooltip="Trunking SKUs are not audited by this tool yet" tooltipPosition="top" />
                  } @else {
                    <span class="bd__zero">0</span>
                  }
                </td>
              }
              <td class="bd__col-actions">
                @if (row.customerId === null) {
                  <p-button
                    icon="pi pi-link"
                    [text]="true"
                    [rounded]="true"
                    severity="warning"
                    pTooltip="Set up mapping for this domain"
                    tooltipPosition="left"
                    (onClick)="goToMappingsForDomain(row.domain)"
                  />
                } @else {
                  <p-button
                    icon="pi pi-arrow-right"
                    [text]="true"
                    [rounded]="true"
                    severity="info"
                    pTooltip="Open in Reconcile"
                    tooltipPosition="left"
                    (onClick)="goToReconcile(row.domain)"
                  />
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="12" class="text-center">No domains found.</td>
            </tr>
          </ng-template>
        </p-table>
      }
    }

    @if (activeView === 'manage') {
      <div class="bd__overview-header">
        <div class="bd__title">
          <p-button
            icon="pi pi-arrow-left"
            [text]="true"
            [rounded]="true"
            severity="secondary"
            styleClass="bd__back-btn"
            (onClick)="closeManage()"
          />
          <i class="pi pi-sitemap" aria-hidden="true"></i>
          <span>Manage Billing Domains</span>
        </div>
        <p-button
          icon="pi pi-cog"
          label="Columns"
          severity="success"
          (onClick)="manageColOverlay.toggle($event)"
        />
        <p-overlayPanel #manageColOverlay>
          <div class="bd__col-overlay">
            <div class="bd__col-overlay-title">Toggle columns</div>
            @for (col of manageColumns; track col.field) {
              <div class="bd__col-overlay-row">
                <p-checkbox
                  [(ngModel)]="col.visible"
                  [binary]="true"
                  [inputId]="'mg-col-' + col.field"
                  (onChange)="onManageColChange()"
                />
                <label [for]="'mg-col-' + col.field" class="cursor-pointer">{{ col.header }}</label>
              </div>
            }
          </div>
        </p-overlayPanel>
      </div>

      <div id="tour-manage-toolbar" class="bd__manage-toolbar">
        <p-button
          icon="pi pi-check"
          label="Enable Selected"
          severity="success"
          [disabled]="!selectedDomains.length"
          (onClick)="bulkEnable()"
        />
        <p-button
          icon="pi pi-times"
          label="Disable Selected"
          severity="danger"
          [disabled]="!selectedDomains.length"
          (onClick)="bulkDisable()"
        />
        <span class="p-input-icon-left bd__manage-search">
          <i class="pi pi-search"></i>
          <input
            pInputText
            type="text"
            placeholder="Search domain, customer or description…"
            [(ngModel)]="manageSearchTerm"
            (ngModelChange)="filterDomains()"
          />
        </span>
      </div>

      @if (manageLoading) {
        <div class="bd__overview-loading">
          <p-progressSpinner styleClass="bd__spinner" strokeWidth="4" />
        </div>
      } @else {
        <p-table
          id="tour-manage-table"
          [value]="filteredDomains"
          [(selection)]="selectedDomains"
          dataKey="domain"
          [paginator]="true"
          [rows]="manageRows"
          [rowsPerPageOptions]="[10, 20, 50, 100]"
          styleClass="p-datatable-sm"
          (onPage)="onManagePageChange($event)"
        >
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 3rem">
                <p-tableHeaderCheckbox />
              </th>
              <th pSortableColumn="domain">Domain <p-sortIcon field="domain" /></th>
              @if (isManageColVisible('customerName')) {
                <th>Customer</th>
              }
              @if (isManageColVisible('description')) {
                <th>Description</th>
              }
              @if (isManageColVisible('userCount')) {
                <th class="bd__col-count" pSortableColumn="userCount">Users <p-sortIcon field="userCount" /></th>
              }
              @if (isManageColVisible('isMapped')) {
                <th
                  pTooltip="Manual = explicit mapping in DB. Blank = auto-matched by name (normal)."
                  tooltipPosition="top"
                >Mapping</th>
              }
              <th>For Billing</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-config>
            <tr>
              <td>
                <p-tableCheckbox [value]="config" />
              </td>
              <td>{{ config.domain }}</td>
              @if (isManageColVisible('customerName')) {
                <td class="bd__col-customer">
                  @if (config.customerId === null) {
                    <p-tag
                      value="No customer found"
                      severity="warning"
                      pTooltip="Could not resolve a Rev.io customer for this domain. Set up a manual mapping to fix it."
                      tooltipPosition="top"
                    />
                  } @else {
                    {{ config.customerName ?? '—' }}
                  }
                </td>
              }
              @if (isManageColVisible('description')) {
                <td class="bd__col-description">{{ config.description ?? '—' }}</td>
              }
              @if (isManageColVisible('userCount')) {
                <td class="bd__col-count">{{ config.userCount ?? '—' }}</td>
              }
              @if (isManageColVisible('isMapped')) {
                <td>
                  @if (config.isMapped) {
                    <p-tag
                      value="Manual"
                      severity="info"
                      pTooltip="This domain has an explicit Rev.io customer mapping configured. Used when auto-match by name is ambiguous or incorrect."
                      tooltipPosition="top"
                    />
                  }
                  <!-- No badge for auto-matched domains — reconcile finds them by name automatically -->
                </td>
              }
              <td>
                <div class="bd__manage-toggle-cell">
                  <p-inputSwitch
                    [(ngModel)]="config.enableForBilling"
                    [disabled]="updatingDomains.has(config.domain) || config.customerId === null"
                    (onChange)="toggleDomain(config)"
                    [pTooltip]="config.customerId === null ? 'No customer mapping — set one up first' : ''"
                  />
                  @if (config.customerId === null) {
                    <p-button
                      icon="pi pi-link"
                      [text]="true"
                      [rounded]="true"
                      severity="warning"
                      size="small"
                      pTooltip="Set up mapping for this domain"
                      tooltipPosition="left"
                      (onClick)="goToMappingsForDomain(config.domain)"
                    />
                  }
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="7" class="text-center">No domains found.</td>
            </tr>
          </ng-template>
        </p-table>
      }
    }

  </section>

</div>
